import { useEffect, useState } from 'react';
import Modal from '../Modal';

/**
 * A modal component for retrieving and selecting banner versions.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {boolean} props.loading - Whether the component is in loading state
 * @param {string} props.templateType - The type of template to load versions for
 * @param {Function} props.loadVersions - Function to load versions based on template type
 * @param {Function} [props.onVersionChange] - Callback fired when version selection changes
 * @param {Function} [props.onConfirm] - Callback fired when user confirms version selection
 * @param {Function} props.onClose - Callback fired when user closes the modal
 * @param {string} [props.title='Scegli la versione del tuo journey'] - Modal title
 * @param {string} [props.versionsLabel='Lista versioni'] - Label for the versions dropdown
 * @param {string} [props.placeholder='Seleziona un ID'] - Placeholder text for the dropdown
 * @returns {JSX.Element|null} The modal component or null if not visible or loading
 */
const RetrieveVersionModal = ({
  visible,
  loading,
  templateType,
  loadVersions,
  onVersionChange,
  onConfirm,
  onClose,
  title = 'Scegli la versione del tuo journey',
  versionsLabel = 'Lista versioni',
  placeholder = 'Seleziona un ID'
}) => {
  const [version, setVersion] = useState('');
  const [savedIds, setSavedIds] = useState([]);

  useEffect(() => {
    if (!visible) return;

    const fetchData = async () => {
      try {
        const versions = await loadVersions(templateType);
        if (Array.isArray(versions)) {
          setSavedIds(versions);
        } else {
          setSavedIds([]);
        }
      } catch (e) {
        console.error('Errore nel recupero versioni', e);
        setSavedIds([]);
      }
    };

    // setVersion('');
    fetchData();
  }, [visible, templateType]);

  const handleChange = (e) => {
    const value = e.target.value;
    setVersion(value);
    if (onVersionChange) {
      onVersionChange(value, templateType);
    }
  };

  const handleConfirm = async () => {
    if (onConfirm && version) {
      await onConfirm(version);
    }
  };

  if (!visible || loading) return null;

  const disabled = savedIds.length === 0;

  return (
    <Modal visible={visible} title={title}>
      {/* BODY */}
      <div className="retrive-banner-version">
        <div className="popup-wrapper">
          <div className="popup-content">
            <div className="form-row">
              <div className="input-wrapper">
                <label className="label" htmlFor="version">
                  {versionsLabel}:&nbsp;
                </label>
                <select
                  id="version"
                  className="input"
                  disabled={disabled}
                  value={version}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden>
                    {placeholder}
                  </option>
                  {savedIds.map((versionObj) => (
                    <option key={versionObj.id} value={versionObj.id}>
                      {versionObj.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="button-container">
        <button
          className="button button--secondary"
          type="button"
          onClick={onClose}
        >
          Ok, ho capito
        </button>
        <button
          className="button button--primary"
          type="button"
          disabled={disabled || !version}
          onClick={handleConfirm}
        >
          VISUALIZZA
        </button>
      </div>
    </Modal>
  );
};

export default RetrieveVersionModal;
